import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useHubEvent } from '@/shared/realtime/useHubEvent';
import { queryKeys } from '@/shared/api/queryKeys';
import type { ScreenBundle } from '@/entities/metadata/api/useScreenBundle';
import { useMetadataWatchStore, isEditLocked } from './model/metadataWatchStore';
import { diffObjectBundle } from './lib/bundleDiff';

/** Octo Lab'da arka arkaya kaydedilen alanlar tek sinyale indirgenir. */
const DEBOUNCE_MS = 700;

/** Kolon vurgusunun ekranda kalma süresi. */
const HIGHLIGHT_MS = 6_000;

/**
 * Özet şeridinin kendiliğinden kalkma süresi.
 *
 * Vurgudan uzun: kolon parlarken cümleyi de okuyabilmeli. Kalıcı bırakmak, otomatik
 * tazelemede kullanıcı hiçbir şey yapmadığı için şeritlerin birikmesi demekti.
 */
const SUMMARY_MS = 20_000;

type BundleEntry = { screenId: string; bundle: ScreenBundle };

/** Önbellekteki tüm ekran paketlerini kimlikleriyle birlikte döndürür. */
export function cachedScreenBundles(qc: QueryClient): BundleEntry[] {
  return qc
    .getQueryCache()
    .findAll({ queryKey: queryKeys.meta.all })
    .flatMap((q) => {
      const key = q.queryKey as readonly unknown[];
      if (key[1] !== 'screenBundle' || typeof key[2] !== 'string') return [];
      const bundle = q.state.data as ScreenBundle | undefined;
      return bundle ? [{ screenId: key[2], bundle }] : [];
    });
}

/** Toplu tazelemede sunucu boş kimlik yollar: hangi nesnelerin değiştiği bilinmiyor. */
const ALL_OBJECTS = '00000000-0000-0000-0000-000000000000';

/** Paketin kapsadığı bütün nesne kimlikleri: master + detaylar. */
function objectIdsOf(bundle: ScreenBundle | undefined): string[] {
  if (!bundle) return [];
  const ids = bundle.object?.objectId ? [bundle.object.objectId] : [];
  for (const detail of Object.values(bundle.detailObjects ?? {})) {
    if (detail.object?.objectId) ids.push(detail.object.objectId);
  }
  return ids;
}

/** Bir ekran paketi verilen nesneyi (master ya da detay) içeriyor mu? */
export function bundleCoversObject(bundle: ScreenBundle, objectId: string): boolean {
  if (objectId === ALL_OBJECTS) return true;
  if (bundle.object?.objectId?.toLowerCase() === objectId) return true;
  return Object.values(bundle.detailObjects ?? {}).some((d) => d.object?.objectId?.toLowerCase() === objectId);
}

/**
 * Octo Lab'da yapılan metadata değişikliğini açık sekmelere duyurur.
 *
 * Sinyal sunucudan geliyor (SystemHub → `MetadataChanged`), yoklama yok.
 *
 * Tazeleme, ekranda düzenleme sürmüyorsa KENDİLİĞİNDEN yapılır: kullanıcı yalnızca
 * bakıyorsa onay istemenin bir karşılığı yok, değişiklik hemen görünsün. Düzenleme
 * varsa (satır içi düzenleme, açık edit form) tazeleme girilen veriyi götüreceği için
 * yapılmaz; onun yerine şerit çıkar ve kararı kullanıcı verir.
 */
export function useMetadataWatch() {
  const qc = useQueryClient();
  const markPending = useMetadataWatchStore((s) => s.markPending);
  const pendingIds = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    timer.current = null;
    const ids = [...pendingIds.current];
    pendingIds.current.clear();
    if (ids.length === 0) return;

    const affected = cachedScreenBundles(qc)
      .filter(({ bundle }) => ids.some((id) => bundleCoversObject(bundle, id)))
      .map(({ screenId }) => screenId);

    // Nesne paketi ayrıca açık olabilir (edit form, lookup); onu doğrudan bayatlatmak
    // güvenli — şerit gerektirmez, bir sonraki okumada tazelenir.
    if (ids.includes(ALL_OBJECTS)) {
      void qc.invalidateQueries({ queryKey: ['meta', 'objectBundle'], refetchType: 'none' });
    } else {
      for (const id of ids) {
        void qc.invalidateQueries({ queryKey: queryKeys.meta.objectBundle(id), refetchType: 'none' });
      }
    }

    const locked = affected.filter((screenId) => isEditLocked(screenId));
    for (const screenId of affected) {
      if (!locked.includes(screenId)) void applyMetadataChange(qc, screenId);
    }
    markPending(locked);
  }, [qc, markPending]);

  useHubEvent(
    'MetadataChanged',
    useCallback(
      (objectId: string) => {
        if (!objectId) return;
        pendingIds.current.add(objectId.toLowerCase());
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(flush, DEBOUNCE_MS);
      },
      [flush],
    ),
  );

  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);
}

/**
 * Şeritteki "Uygula" eylemi: paketi yeniden çeker, eski hâliyle karşılaştırır ve farkı
 * vurgulanmak üzere saklar.
 */
export async function applyMetadataChange(qc: QueryClient, screenId: string): Promise<void> {
  const key = queryKeys.meta.screenBundle(screenId);
  const before = qc.getQueryData<ScreenBundle>(key);

  await qc.refetchQueries({ queryKey: key, exact: true });
  const after = qc.getQueryData<ScreenBundle>(key);

  // Veri sorgusu da tazelenir: kolon listesi ve sırası SELECT yanıtından geliyor, yani
  // yalnızca metadata paketini yenilemek yeni eklenen bir alanı görünür kılmıyor ve
  // değişen sırayı grid'e taşımıyordu.
  for (const objectId of objectIdsOf(after)) {
    void qc.invalidateQueries({ queryKey: queryKeys.data.object(objectId) });
  }

  const diff = diffObjectBundle(before, after);
  const store = useMetadataWatchStore.getState();
  store.setApplied(screenId, diff.items, diff.highlightFields);

  if (diff.highlightFields.length > 0) {
    // clearHighlights, setApplied DEĞİL: bu arada yeni bir sinyal gelip ekranı "bekliyor"
    // işaretlemiş olabilir ve setApplied o bayrağı ezip uyarıyı görünmez kılardı.
    setTimeout(() => useMetadataWatchStore.getState().clearHighlights(screenId), HIGHLIGHT_MS);
  }

  if (diff.items.length > 0) {
    setTimeout(() => {
      // Bu arada yeni bir değişiklik geldiyse (pending) şerit kalmalı.
      const current = useMetadataWatchStore.getState().screens[screenId.toLowerCase()];
      if (current && !current.pending) useMetadataWatchStore.getState().dismiss(screenId);
    }, SUMMARY_MS);
  }
}
